import { Hex, keccak256, toHex } from 'viem'
import { useMemo } from 'react'
import { makeReadSurvey4Contract } from './useReadSurvey4Contract'
import { useAccount } from 'wagmi'

/*
 * Fetches the data related to a user's submitted answer from the Survey4 contract
 * @param survey4Address The Survey4 contract address
 * @returns The user's submitted answer and its weight
 */
export function useUserSubmittedAnswer(survey4Address: Hex) {
	const { address: userAddress = '0x', isConnected } = useAccount()
	const useSurvey4ReadContract = makeReadSurvey4Contract(survey4Address)

	// Fetch user submitted answer hash
	const {
		data: userSubmittedAnswerHashes = [],
		refetch: refetchUserAnswers,
		isPending: isGetUserAnswersPending
	} = useSurvey4ReadContract({
		functionName: 'getUserAnswers',
		args: [userAddress],
		query: {
			enabled: isConnected
		}
	})
	const userSubmittedAnswerHash = userSubmittedAnswerHashes[0]

	// Fetch all answers from the survey
	const {
		data: survey4Answers = [],
		refetch: refetchSurveyAnswers,
		isPending: isGetAnswersPending
	} = useSurvey4ReadContract({
		functionName: 'getAnswers',
		args: [],
		query: {
			enabled: isConnected
		}
	})

	// Fetch user submitted answer weight - only if we have a hash
	const {
		data: userSubmittedAnswerWeight = [],
		refetch: refetchUserVoteWeight,
		isPending: isGetUserVoteByAnswerPending
	} = useSurvey4ReadContract({
		functionName: 'getUserVoteByAnswer',
		args: [userAddress, userSubmittedAnswerHash],
		query: {
			enabled: !!userSubmittedAnswerHash && isConnected
		}
	})
	const [, userSubmittedAnswerWeightValue = 0n] = userSubmittedAnswerWeight

	// Find the user's submitted answer by comparing hashes
	const userSubmittedAnswer = useMemo(() => {
		if (!survey4Answers || !userSubmittedAnswerHash) return null

		return (
			survey4Answers.find(answer => {
				const hash = keccak256(toHex(answer.id))
				return hash === userSubmittedAnswerHash
			}) || null
		)
	}, [survey4Answers, userSubmittedAnswerHash])

	// Refetch function that refreshes all data
	const refetch = async () =>
		await Promise.all([refetchUserAnswers(), refetchUserVoteWeight(), refetchSurveyAnswers()])

	return {
		userSubmittedAnswer: userSubmittedAnswer?.id,
		userSubmittedAnswerWeight: userSubmittedAnswerWeightValue,
		isPending:
			isConnected &&
			(isGetUserAnswersPending ||
				(isGetUserVoteByAnswerPending && !!userSubmittedAnswerHash) ||
				isGetAnswersPending),
		refetch
	}
}
